
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import {
	decodeCrosswordClue,
	encodeCrosswordClue,
	getCrosswordPublisherByCrosswordId,
} from "@utils";
import {
	AD_LAYOUT_STUBS,
	PRODUCTION_ORIGIN,
	CROSSWORD_PUBLISHER_DATA,
} from "@consts";
import scrollTriggeredModalMixin from "@/plugins/mixins/scroll-triggered-modal";

dayjs.extend(utc);
dayjs.extend(tz);

// Shared data fetching function
const fetchClueData = async ({ axios, config, params, store }) => {
	// Check if there is a clue param first.
	const clueSlug = params?.clue;
	if (!clueSlug) {
		throw new Error("No clue provided");
	}

	// Decode the clue from the URL and fetch the first clue.
	const query = decodeCrosswordClue(clueSlug);
	const { data } = await axios.get(
		`${config.CWH_API_SERVICE}/crossword/v1/answers`,
		{
			responseType: "json",
			params: {
				query,
				max: 150,
				offset: 0,
			},
		}
	);

	// If clue was not found, throw an error.
	if (!data?.first?.clue) {
		throw new Error("Clue not found");
	}

	// If the clue is too old, throw an error.
	const OLDEST_ALLOWED_DATE = "2022-01-01";
	if (dayjs(data.first.date).isBefore(OLDEST_ALLOWED_DATE)) {
		throw new Error("This clue is too old.");
	}

	// Check if the clue matches the URL. If the case does not match, redirect to the correct URL.
	const decodedClue = decodeCrosswordClue(params.clue);
	if (data.first?.clue && data.first.clue !== decodedClue) {
		return {
			redirect: true,
			encodedClue: encodeCrosswordClue(data.first.clue),
		};
	}

	const publisherData = getCrosswordPublisherByCrosswordId(
		data.first.crosswordId
	);

	// Fetch other clues for the same crossword and date as the first clue (if publisher is one of the approved ones)
	const cluesResponse = await axios.get(
		`${config.CWH_API_SERVICE}/daily-crosswords/v1/crossword`,
		{
			responseType: "json",
			params: {
				crosswordId: data.first.crosswordId,
				date: data.first.date,
			},
		}
	);

	const { answers: cluesAnswers } = cluesResponse.data.crossword || {};
	const { across, down } = cluesAnswers || {};

	const cluesData = {
		across,
		down,
	};

	const breadcrumbs = [
		{ label: "CrosswordSolverBreadcrumb", url: "/crosswords/" },
		{
			label: "Clue",
		},
	];

	if (publisherData) {
		breadcrumbs.splice(1, 0, {
			label: publisherData.breadcrumbs,
			url: `/crosswords/${publisherData.slug}/`,
		});
	}

	store.commit("navigation/setBreadcrumbsOverride", breadcrumbs);

	const answers = data.answers.map((a) => ({
		answer: a.answer,
		score: Math.floor(a.score),
		isExactMatch: Math.floor(a.score) === 100,
	}));

	store.commit("clues/SET_FILTERED_ANSWERS", answers);
	store.commit("clues/SET_ALL_ANSWERS", answers);

	return {
		redirect: false,
		data: {
			clueData: data.first,
			cluesData,
			publisher: publisherData,
		},
	};
};

export default {
	name: "CrosswordClue",
	mixins: [scrollTriggeredModalMixin],
	// asyncData: server-only (it returns straight away on client nav, so it
	// never blocks the route transition). Unlike the other pages in this
	// epic, the clue lookup has to stay here on the server: the 301 redirect
	// and Expires header need `res`, and the publisher breadcrumbs it commits
	// must be in the store before the layout renders them above the page.
	async asyncData({ res, $axios, $config, error, params, store, redirect }) {
		if (!process.server) {
			return;
		}

		let result;
		try {
			result = await fetchClueData({
				axios: $axios,
				config: $config,
				params,
				store,
			});
		} catch (e) {
			error({
				statusCode: 404,
				message: e.message,
			});
			return;
		}

		if (result.redirect) {
			redirect(301, `/crosswords/clues/${result.encodedClue}/`);
			return;
		}

		// Set cache expiration header:
		if (res) {
			const expirationDate = dayjs()
				.tz("America/New_York")
				.add(1, "year")
				.utc()
				.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");

			res.setHeader("Expires", expirationDate);
		}

		return result.data;
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			clueData: null,
			cluesData: null,
			publisher: null,
		};
	},
	// fetch: client nav only (the server load happens in asyncData above).
	// Non-blocking, so navigating to a clue no longer freezes the route
	// transition.
	async fetch() {
		if (process.server) {
			return;
		}

		await this.loadData();
	},
	head({ $buildMetaDataObject }) {
		const noindex = [];
		try {
			const dateString = this.clueData?.date;
			const date = dayjs(dateString || undefined);
			if (date.isBefore(dayjs().subtract(10, "days"), "days")) {
				noindex.push({ name: "robots", content: "noindex" });
			}
		} catch (error) {}

		const { publisherId } = this.clueData || {};

		let publisherMetaTitle;
		let publisherMetaDescription;

		switch (publisherId) {
			case "ARP":
				publisherMetaTitle = "AARP";
				publisherMetaDescription = "AARP";
				break;
			case "USA":
				publisherMetaTitle = "USAT";
				publisherMetaDescription = "USA Today";
				break;
			case "LAT":
				publisherMetaTitle = "LAT";
				publisherMetaDescription = "Los Angeles Times";
				break;
			default:
				publisherMetaTitle = "NYT";
				publisherMetaDescription = "The New York Times";
		}

		const meta = $buildMetaDataObject(
			{
				title: `${this.clueText} | ${publisherMetaTitle} Crossword Clue Answers`,
				description: `Find all the answers for today's ${publisherMetaDescription} crossword, including the answers to the "${this.clueText}" Crossword Clue.`,
			},
			noindex
		);

		const clueText = this.clueData?.clue;

		if (clueText) {
			return {
				...meta,
				link: [
					{
						rel: "canonical",
						href: `${PRODUCTION_ORIGIN}/crosswords/clues/${encodeCrosswordClue(
							clueText
						)}/`,
					},
				],
			};
		}

		return meta;
	},
	computed: {
		otherCluesPublisherData() {
			const iconPath =
				this.publisher?.iconPath ||
				CROSSWORD_PUBLISHER_DATA[0].iconPath;
			const shortTitle =
				this.publisher?.shortTitle ||
				`${this.clueData?.publisherId} - ${dayjs(
					this.clueData?.date
				).format("MMMM D")}`;

			return {
				iconPath,
				shortTitle,
			};
		},
		clueText() {
			try {
				return decodeCrosswordClue(this.$route.params.clue);
			} catch (e) {
				return "";
			}
		},
		lastSeenText() {
			try {
				const { source, date } = this.clueData || {};

				const dateObject = dayjs(date);
				if (!dateObject.isValid()) {
					throw new Error("Invalid last seen date");
				}
				const formattedDate = dateObject.format("D MMM YYYY");

				return `${source}, ${formattedDate}`;
			} catch (e) {
				return "";
			}
		},
		answerCountText() {
			const count = this.answers?.length || 0;
			if (!count) {
				return "";
			}

			if (count === 1) {
				return "Matching Answer";
			}

			return `Showing ${count} Matching Answers`;
		},
		// This ensures that the directions are always in the correct order.
		clueGroups() {
			const groups = [];

			["across", "down"].forEach((direction) => {
				if (!this.cluesData?.[direction]?.length) {
					return;
				}

				const pathBase = `/crosswords/clues`;
				groups.push({
					direction,
					clues: (this.cluesData?.[direction] || [])
						.filter((clue) => {
							return clue.clue !== this.clueData.clue;
						})
						.map((clue) => ({
							...clue,
							path: `${pathBase}/${encodeCrosswordClue(
								clue.clue
							)}`,
						})),
				});
			});

			return groups;
		},
		answers() {
			return this.$store.state.clues.filteredAnswers;
		},
	},
	methods: {
		async loadData() {
			let result;
			try {
				result = await fetchClueData({
					axios: this.$axios,
					config: this.$config,
					params: this.$route.params,
					store: this.$store,
				});
			} catch (e) {
				this.$nuxt.error({
					statusCode: 404,
					message: e.message,
				});
				return;
			}

			if (result.redirect) {
				this.$router.replace(
					`/crosswords/clues/${result.encodedClue}/`
				);
				return;
			}

			Object.assign(this, result.data);
		},
		formatDate(format, date) {
			const d = date ? dayjs(date) : dayjs();
			return d.format(format);
		},
	},
};
